import { RANGE_SCALES_NM } from "../ppi/coords.js";
import type { DisplayState } from "../ppi/displayState.js";
import type { ChromeProps } from "./types.js";

/**
 * Garmin GPSMAP / Fantom-style chrome: dark bezel, range scale switcher
 * along the right edge, gain / sea / rain / tune knobs along the bottom.
 * Visual style is a pastiche; trademark assets live with the device, not in
 * a teaching simulator.
 */
export function GarminGPSMAP(props: ChromeProps): JSX.Element {
  const { ppi, display, onDisplayChange, scenarioName, paused } = props;

  const updateDisplay = (patch: Partial<DisplayState>) => {
    onDisplayChange({ ...display, ...patch });
  };

  return (
    <div
      style={{
        background: "#1a1d20",
        color: "#cbe8d0",
        fontFamily: "ui-monospace, monospace",
        padding: 18,
        borderRadius: 14,
        boxShadow: "0 0 0 8px #2a2d31, 0 0 24px rgba(0,0,0,0.4)",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 120px",
        gridTemplateRows: "auto minmax(0, 1fr) auto",
        gap: 12,
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <Header scenarioName={scenarioName} paused={paused} />
      <div
        style={{
          gridColumn: "1 / 2",
          gridRow: "2 / 3",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 0,
          minHeight: 0,
        }}
      >
        {ppi}
      </div>
      <RangePanel
        rangeNm={display.rangeNm}
        onChange={(rangeNm) => updateDisplay({ rangeNm })}
      />
      <ControlBar display={display} onChange={updateDisplay} />
    </div>
  );
}

function Header(props: {
  scenarioName: string;
  paused: boolean | undefined;
}): JSX.Element {
  return (
    <div
      style={{
        gridColumn: "1 / 3",
        gridRow: "1 / 2",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        borderBottom: "1px solid #2a2d31",
        paddingBottom: 8,
      }}
    >
      <div>
        <strong style={{ fontSize: 13, letterSpacing: 1 }}>GPSMAP 8617</strong>
        <span style={{ opacity: 0.6, marginLeft: 12 }}>RADAR · MARPA · ARPA</span>
      </div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>
        {props.paused ? "PAUSED · " : ""}
        {props.scenarioName}
      </div>
    </div>
  );
}

function RangePanel(props: {
  rangeNm: number;
  onChange: (rangeNm: number) => void;
}): JSX.Element {
  return (
    <div
      style={{
        gridColumn: "2 / 3",
        gridRow: "2 / 3",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 4 }}>RANGE (nm)</div>
      {RANGE_SCALES_NM.map((r) => {
        const active = Math.abs(r - props.rangeNm) < 1e-6;
        return (
          <button
            key={r}
            onClick={() => props.onChange(r)}
            style={{
              background: active ? "#2f5c3f" : "transparent",
              border: "1px solid #2a4a35",
              color: "#cbe8d0",
              fontFamily: "inherit",
              fontSize: 12,
              padding: "4px 8px",
              cursor: "pointer",
              borderRadius: 4,
              textAlign: "left",
            }}
          >
            {r}
          </button>
        );
      })}
    </div>
  );
}

function ControlBar(props: {
  display: DisplayState;
  onChange: (patch: Partial<DisplayState>) => void;
}): JSX.Element {
  const { display, onChange } = props;
  return (
    <div
      style={{
        gridColumn: "1 / 3",
        gridRow: "3 / 4",
        display: "grid",
        // auto-fit so the row wraps to a second row on narrow chromes
        // rather than overflowing the column. minmax(80px, 1fr) gives
        // each control a sensible minimum without being too cramped.
        gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
        gap: 12,
        alignItems: "center",
        borderTop: "1px solid #2a2d31",
        paddingTop: 12,
        minWidth: 0,
      }}
    >
      <Knob
        label="GAIN"
        value={display.gain}
        onChange={(gain) => onChange({ gain })}
      />
      <Knob
        label="SEA"
        value={display.sea}
        onChange={(sea) => onChange({ sea })}
      />
      <Knob
        label="RAIN"
        value={display.rain}
        onChange={(rain) => onChange({ rain })}
      />
      <Knob
        label="TUNE"
        value={display.tune}
        onChange={(tune) => onChange({ tune })}
      />
      <ModeButton
        label="MODE"
        value={display.mode}
        onChange={(mode) => onChange({ mode })}
      />
      <ToggleColumn label="LIGHTS">
        <button
          onClick={() => onChange({ nightMode: !display.nightMode })}
          style={chipStyle(display.nightMode, "#5c4a2f")}
          title="Night-mode lit-mark pulsing — instructional, not a standard radar facility"
        >
          {display.nightMode ? "ON" : "OFF"}
        </button>
      </ToggleColumn>
      <ToggleColumn label="MARKS">
        <button
          onClick={() => onChange({ showMarks: !display.showMarks })}
          style={chipStyle(display.showMarks, "#2f5c3f")}
          title="Show/hide navigation marks (buoys, towers, racing buoys) on radar and chart"
        >
          {display.showMarks ? "ON" : "OFF"}
        </button>
      </ToggleColumn>
      <ToggleColumn label="EBL">
        {display.ebl.map((e) => (
          <button
            key={e.id}
            onClick={() =>
              onChange({
                ebl: display.ebl.map((x) =>
                  x.id === e.id ? { ...x, visible: !x.visible } : x,
                ),
              })
            }
            style={chipStyle(e.visible)}
          >
            EBL{e.id}
          </button>
        ))}
      </ToggleColumn>
      <ToggleColumn label="VRM">
        {display.vrm.map((v) => (
          <button
            key={v.id}
            onClick={() =>
              onChange({
                vrm: display.vrm.map((x) =>
                  x.id === v.id ? { ...x, visible: !x.visible } : x,
                ),
              })
            }
            style={chipStyle(v.visible)}
          >
            VRM{v.id}
          </button>
        ))}
      </ToggleColumn>
      {/* Pause/Resume lives on the helm panel — duplicating it here
          ate a column and pushed EBL/VRM off the chrome on narrow
          viewports. */}
    </div>
  );
}

function chipStyle(active: boolean, activeBg = "#2f5c3f"): React.CSSProperties {
  return {
    background: active ? activeBg : "transparent",
    border: "1px solid #2a4a35",
    color: "#cbe8d0",
    fontFamily: "inherit",
    fontSize: 11,
    padding: "4px 6px",
    cursor: "pointer",
    borderRadius: 3,
    flex: 1,
  };
}

function ToggleColumn(props: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <span style={{ fontSize: 10, opacity: 0.7 }}>{props.label}</span>
      <div style={{ display: "flex", gap: 4, minWidth: 0 }}>
        {props.children}
      </div>
    </div>
  );
}

function Knob(props: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <span style={{ fontSize: 10, opacity: 0.7 }}>{props.label}</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={props.value}
        onChange={(e) => props.onChange(parseFloat(e.target.value))}
        // minWidth: 0 lets the slider actually shrink below its
        // ~129px UA default; without it the four sliders bullied the
        // chrome wider than its grid column.
        style={{ accentColor: "#5a8a6a", minWidth: 0, width: "100%" }}
      />
      <span style={{ fontSize: 10, opacity: 0.6 }}>
        {(props.value * 100).toFixed(0)}
      </span>
    </label>
  );
}

function ModeButton(props: {
  label: string;
  value: DisplayState["mode"];
  onChange: (v: DisplayState["mode"]) => void;
}): JSX.Element {
  const cycle = (): DisplayState["mode"] =>
    props.value === "head-up"
      ? "north-up"
      : props.value === "north-up"
        ? "course-up"
        : "head-up";
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 10, opacity: 0.7 }}>{props.label}</span>
      <button
        onClick={() => props.onChange(cycle())}
        style={chipStyle(true, "#2f4d5c")}
      >
        {props.value.toUpperCase()}
      </button>
    </label>
  );
}
